import { Box } from '@mui/material';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { DragHintText, DragReadoutCard } from './DragReadoutCard';
import { useDragReorder, usePlanEditor } from './editor';
import { PlanRow } from './PlanRow';
import { COLUMN_WIDTH, ROW_ESTIMATE_PX } from './treeGeometry';

import { MaterialSymbol } from '@/components/MaterialSymbol';
import { MICRO_LABEL_TRACKING } from '@/components/ui';
import { zIndex } from '@/theme';

/**
 * The virtualized row list (BR-3: plans reach tens of thousands of rows).
 * The scroll element is the panel's scroller, passed down from the page;
 * the open detail card gets true measurement via measureElement.
 */

const HeaderCell = ({
  width,
  label,
  align,
  gutterLeft,
}: {
  width?: number;
  label?: string;
  align?: 'right';
  /** Mirrors a row cell's extra left margin, so header and row stay aligned. */
  gutterLeft?: number;
}) => (
  <Box
    component="span"
    sx={{
      width,
      ...(gutterLeft === undefined ? {} : { ml: `${String(gutterLeft)}px` }),
      flex: width ? 'none' : 1,
      textAlign: align,
      /*
        One line, overflowing toward the start: "Budget hours" is wider than the
        78px hours column, and a right-aligned flex end spills left into the
        task column's free space instead of wrapping to two lines.
      */
      whiteSpace: 'nowrap',
      ...(align === 'right' ? { display: 'flex', justifyContent: 'flex-end' } : {}),
      fontSize: 'var(--font-size-label)',
      fontWeight: 'var(--font-weight-regular)',
      letterSpacing: MICRO_LABEL_TRACKING,
      textTransform: 'uppercase',
      color: 'var(--fg-3)',
    }}
  >
    {label}
  </Box>
);

export type PlanTreeProps = {
  scrollElement: HTMLElement | null;
};

export const PlanTree = ({ scrollElement }: PlanTreeProps) => {
  const { t } = useTranslation(['pmPlanning', 'pmCommon', 'projectManagement']);
  const { flat, state, dispatch } = usePlanEditor();
  const rows = flat.rows;
  const { cardRef, draggingId, indicator, intoId, outcome, startDrag } =
    useDragReorder(scrollElement);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollElement,
    estimateSize: () => ROW_ESTIMATE_PX,
    overscan: 12,
    getItemKey: (index) => rows[index]!.id,
  });

  // Keep the row being edited (or navigated to) in view.
  const focusId = state.editing?.nodeId ?? state.activeId;
  useEffect(() => {
    if (!focusId) return;
    const index = flat.index.rowIndexById.get(focusId);
    if (index !== undefined) virtualizer.scrollToIndex(index);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- scroll on focus change only
  }, [focusId]);

  return (
    <Box data-automation-id="plan-tree">
      {/* column header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1,
          borderBottom: 'var(--border-width-thin) solid var(--border-subtle)',
          position: 'sticky',
          top: 0,
          bgcolor: 'var(--bg-panel)',
          zIndex: zIndex.stickyHeader,
        }}
      >
        <Box
          component="button"
          type="button"
          title={t('tree.SelectAll')}
          data-automation-id="select-all"
          onClick={() => {
            const allSelected =
              rows.length > 0 && rows.every((row) => state.selectedIds.has(row.id));
            dispatch(
              allSelected
                ? { type: 'CLEAR_SELECTION' }
                : { type: 'SET_SELECTION', ids: rows.map((row) => row.id) },
            );
          }}
          sx={{
            width: COLUMN_WIDTH.select,
            flex: 'none',
            display: 'flex',
            justifyContent: 'center',
            border: 'none',
            bgcolor: 'transparent',
            p: 0,
            cursor: 'pointer',
          }}
        >
          <MaterialSymbol
            name={
              rows.length > 0 && rows.every((row) => state.selectedIds.has(row.id))
                ? 'check_box'
                : state.selectedIds.size > 0
                  ? 'indeterminate_check_box'
                  : 'check_box_outline_blank'
            }
            size={18}
            color={state.selectedIds.size > 0 ? 'var(--color-blue-80)' : 'var(--fg-3)'}
          />
        </Box>
        <Box component="span" sx={{ width: 18, flex: 'none' }} />
        <HeaderCell label={t('pmCommon:term.Task')} />
        <HeaderCell
          width={COLUMN_WIDTH.hours}
          label={t('pmCommon:term.BudgetHours')}
          align="right"
        />
        <HeaderCell
          width={COLUMN_WIDTH.startDate}
          label={t('pmCommon:term.StartDate')}
          align="right"
        />
        <HeaderCell
          width={COLUMN_WIDTH.targetDate}
          label={t('pmCommon:term.TargetDate')}
          align="right"
        />
        <HeaderCell width={COLUMN_WIDTH.trailing} />
      </Box>

      {/*
        No "+ Add task" above the rows. It sat directly under the column header
        and landed its task INSIDE the first top-level row, which is not what a
        control at the top of a list looks like it will do — pilot users kept
        hitting it by accident. The ghost row at the foot of the list
        (`ghost-add-task-row`) and the toolbar's "Add task" both remain, and
        both append where you would expect.
      */}
      <Box sx={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
        {virtualizer.getVirtualItems().map((item) => {
          const row = rows[item.index]!;
          return (
            <Box
              key={item.key}
              ref={virtualizer.measureElement}
              data-index={item.index}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${item.start}px)`,
              }}
            >
              <PlanRow
                row={row}
                dragging={draggingId === row.id}
                dropInto={intoId === row.id}
                onDragHandleDown={startDrag(row.id)}
              />
            </Box>
          );
        })}
      </Box>

      {indicator ? (
        <Box
          data-automation-id="drop-indicator"
          data-state={indicator.refused ? 'refused' : 'ready'}
          sx={{
            position: 'fixed',
            top: indicator.top - 1,
            left: indicator.left,
            width: indicator.width,
            height: 2,
            // Coral says the drop will not happen — the same vocabulary the
            // timeline's readout uses.
            ...(indicator.refused
              ? { borderTop: '2px dashed var(--color-coral-80)' }
              : { bgcolor: 'var(--color-blue-80)' }),
            borderRadius: 'var(--border-radius-small)',
            zIndex: zIndex.dropIndicator,
            pointerEvents: 'none',
          }}
        />
      ) : null}

      {/*
        The words for what the line means. The list had only the line, and a
        22px difference in its indent was the entire distinction between
        reordering and nesting — see `DragReadoutCard`.
      */}
      {draggingId ? (
        <DragReadoutCard
          cardRef={cardRef}
          name={flat.index.nodeById.get(draggingId)?.name ?? ''}
          descendantCount={flat.index.descendantCountById.get(draggingId) ?? 0}
          outcome={outcome}
          idPrefix="plan"
          levelHint={<DragHintText>{t('tree.LevelHint')}</DragHintText>}
        />
      ) : null}
    </Box>
  );
};
